import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, Clock, Filter, Sparkles, Star, Target, TrendingUp, Users } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from '../components/GlobalPhoebeChat';
import { ResetFiltersButton } from '../components/ResetFiltersButton';
type Priority = 'critical' | 'warning' | 'positive';
type Insight = {
  id: number;
  skill: string;
  headline: string;
  priority: Priority;
  affectedLearners: number;
  metrics: {
    label: string;
    value: string;
  }[];
  signals: string[];
  actions: string[];
  timestamp: string;
  learner?: string;
};
const insights: Insight[] = [{
  id: 1,
  skill: 'CSS Fundamentals',
  headline: '50% first-attempt failure rate — below the 70% pass threshold',
  priority: 'critical',
  affectedLearners: 10,
  metrics: [{
    label: 'Failing',
    value: '10 of 20 learners'
  }, {
    label: 'Average score',
    value: '54%'
  }, {
    label: 'Pass threshold',
    value: '70%'
  }],
  signals: ['Questions 4–7 are most missed', 'High confidence with low accuracy', 'Average completion is only 12 minutes'],
  actions: ['Email the 10 learners who are failing', 'Schedule a group study session', 'Recommend how these learners should prepare'],
  timestamp: 'Detected 2 hours ago'
}, {
  id: 2,
  skill: 'JavaScript Basics',
  headline: '34% confident error rate on async/await concepts — 2.8× above average',
  priority: 'critical',
  affectedLearners: 89,
  metrics: [{
    label: 'Learners affected',
    value: '89'
  }, {
    label: 'Confident error rate',
    value: '34%'
  }, {
    label: 'Attempts analyzed',
    value: '156'
  }],
  signals: ['Question 6 has the highest error rate', 'Confident but incorrect responses are increasing', '67% abandon the question mid-attempt'],
  actions: ['Show the 89 learners affected', 'Draft a study nudge for these learners', 'Suggest what these learners should review first'],
  timestamp: 'Detected today'
}, {
  id: 3,
  skill: 'Responsive Design',
  headline: 'Enrollment is at 12% despite being a core learning requirement',
  priority: 'warning',
  affectedLearners: 218,
  metrics: [{
    label: 'Enrolled',
    value: '30 of 248'
  }, {
    label: 'Started this month',
    value: '8'
  }, {
    label: 'Completion rate',
    value: '63%'
  }],
  signals: ['Learners are unsure what to complete before starting', 'Assigned learners are not returning after their first visit', 'Interest drops after the first preview'],
  actions: ['Send a targeted enrollment message', 'Show learners who have not started', 'Offer a short orientation session'],
  timestamp: 'Detected yesterday'
}, {
  id: 4,
  skill: 'HTML Basics',
  headline: 'First-attempt pass rate rose 23% where learners used the video resources',
  priority: 'positive',
  affectedLearners: 124,
  metrics: [{
    label: 'Pass rate',
    value: '87%'
  }, {
    label: 'Change',
    value: '+23%'
  }, {
    label: 'Average completion',
    value: '3.1 attempts'
  }],
  signals: ['Videos are mentioned in 78% of positive learner feedback', 'Completion time decreased by 26%', 'Strong gains across all learner groups'],
  actions: ['Recognize high performers', 'Point CSS learners to the video resources', 'Share what worked with other learners'],
  timestamp: 'Observed over 3 weeks'
}, {
  id: 5,
  skill: 'Multiple Skills',
  headline: 'Alex Rivera is consistently scoring below 50% across five attempts',
  priority: 'warning',
  affectedLearners: 1,
  learner: 'Alex Rivera',
  metrics: [{
    label: 'Average score',
    value: '45%'
  }, {
    label: 'Attempts',
    value: '5'
  }, {
    label: 'Last activity',
    value: '3 days ago'
  }],
  signals: ['Foundational gaps across modules', 'High confident error indicator', 'Scores are not improving on retakes'],
  actions: ['Schedule a 1-on-1 check-in', 'Identify what is blocking Alex', 'Build a personal study plan for Alex'],
  timestamp: 'Detected 3 days ago'
}, {
  id: 6,
  skill: 'Organization Overview',
  headline: 'Learning participation is gaining momentum',
  priority: 'positive',
  affectedLearners: 198,
  metrics: [{
    label: 'Monthly participation',
    value: '80%'
  }, {
    label: 'Change',
    value: '+20% vs 30 days ago'
  }, {
    label: 'Learners active this month',
    value: '198 of 248'
  }],
  signals: ['Monthly participation has reached 80% of learners.', 'Learning activity is up 20% compared with the prior 30-day period.', 'A timely reminder can help convert active learners into completed modules.'],
  actions: ['Send a completion reminder to active learners', 'Identify learners close to completing a module', 'Review active learner progress by skill'],
  timestamp: 'Observed this month'
}, {
  id: 7,
  skill: 'Organization Overview',
  headline: 'Completion needs a focused follow-up',
  priority: 'warning',
  affectedLearners: 223,
  metrics: [{
    label: 'Completed all modules',
    value: '10%'
  }, {
    label: 'Change',
    value: '-20% vs 30 days ago'
  }, {
    label: 'Learners needing follow-up',
    value: '223 of 248'
  }],
  signals: ['Only 10% of learners have completed every skill module.', 'Completion fell 20% from the prior 30-day period.', 'Active learners with one or two modules remaining are the strongest follow-up opportunity.'],
  actions: ['Send a targeted completion reminder', 'Identify learners with one or two modules remaining', 'Schedule a completion support check-in'],
  timestamp: 'Detected this month'
}, {
  id: 8,
  skill: 'CSS Fundamentals',
  headline: 'CSS Fundamentals is a priority support opportunity',
  priority: 'critical',
  affectedLearners: 10,
  metrics: [{
    label: 'Confident error rate',
    value: '22%'
  }, {
    label: 'Support focus',
    value: 'Flexbox, Grid, positioning'
  }, {
    label: 'Priority',
    value: 'Above other core modules'
  }],
  signals: ['CSS Fundamentals has a 22% confident error rate, above the other core modules.', 'Learners are most often confident but incorrect on Flexbox, Grid, and positioning.', 'Encouraging more practice time on these topics before the next attempt can improve readiness.'],
  actions: ['Show learners struggling with CSS Fundamentals', 'Draft a study nudge for these learners', 'Recommend how these learners should prepare'],
  timestamp: 'Detected today'
}, {
  id: 9,
  skill: 'Assessment Readiness',
  headline: 'First-attempt readiness needs attention',
  priority: 'critical',
  affectedLearners: 248,
  metrics: [{
    label: 'Average first-attempt score',
    value: '64.93%'
  }, {
    label: 'Pass threshold',
    value: '70%'
  }, {
    label: 'Readiness gap',
    value: '5.07 percentage points'
  }],
  signals: ['Average first-attempt score is 64.93%, below the 70% pass threshold.', 'Confident incorrect responses show learners are attempting before they are ready.', 'Encouraging learners to prepare before their first attempt reduces unnecessary retakes.'],
  actions: ['Show learners attempting before they are ready', 'Recommend how learners should prepare before a first attempt', 'Draft a readiness reminder before retakes'],
  timestamp: 'Detected today'
}];
const learners = ['All Learners', 'Alex Rivera', 'Emily Davis', 'John Smith', 'Lisa Wong', 'Michael Chen', 'Sarah Johnson'];
export function InsightsActionsPage() {
  const location = useLocation();
  const [priority, setPriority] = useState<'all' | Priority>('all');
  const [skill, setSkill] = useState('All Skills');
  const [learner, setLearner] = useState('All Learners');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const skills = useMemo(() => ['All Skills', ...Array.from(new Set(insights.map(item => item.skill))).sort()], []);
  useEffect(() => {
    const insightId = Number(new URLSearchParams(location.search).get('insight'));
    if (!insights.some(insight => insight.id === insightId)) return;
    setPriority('all');
    setSkill('All Skills');
    setLearner('All Learners');
    setExpanded(new Set([insightId]));
    window.requestAnimationFrame(() => {
      document.getElementById(`insight-${insightId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });
  }, [location.search]);
  const scoped = insights.filter(item => learner === 'All Learners' ? !item.learner : item.learner === learner);
  const visible = scoped.filter(item => (priority === 'all' || item.priority === priority) && (skill === 'All Skills' || item.skill === skill));
  const counts = {
    critical: scoped.filter(item => item.priority === 'critical').length,
    warning: scoped.filter(item => item.priority === 'warning').length,
    positive: scoped.filter(item => item.priority === 'positive').length
  };
  const toggleInsight = (insight: Insight) => {
    setExpanded(current => {
      const next = new Set(current);
      next.has(insight.id) ? next.delete(insight.id) : next.add(insight.id);
      return next;
    });
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-2567" data-mp="2sg04duejv1jq">
      <div className="border-b border-gray-200 bg-white" data-id="element-2568" data-mp="2sh1pkrpa54f6">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8" data-id="element-2569" data-mp="2nhypzms2jqel">
          <Link to="/admin" className="inline-flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900" data-id="element-2570" data-mp="32fr0g0irdolx">
            <ArrowLeft size={16} data-id="element-2571" data-mp="hn5rybd8q0al" /> Admin Hub
          </Link>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#30718D]" data-id="element-2572" data-mp="cpx4pwd8j62h">
            <Sparkles size={16} data-id="element-2573" data-mp="k96h7yh1fkvr" /> AI insights
          </span>
        </div>
      </div>
      <main className="mx-auto max-w-[1440px] px-4 py-7 sm:px-6 lg:px-8" data-id="element-2574" data-mp="p7zubpsex9ve">
        <header data-phoebe-tour="insights" className="mb-6" data-id="element-2575" data-mp="p5wsm8hwdsau">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl" data-id="element-2576" data-mp="b99uw3i4ey3v">
            Insights &amp; Actions
          </h1>
          <p className="mt-1 text-sm text-gray-600" data-id="element-2577" data-mp="fabuilfy9c6d">
            AI-powered analysis of learning patterns across your organization.
          </p>
        </header>

        <section aria-label="Insight summary" className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4" data-id="element-2578" data-mp="17yqxg9lt441c">
          <StatCard label="Skills monitored" value={String(new Set(scoped.map(item => item.skill)).size)} icon={<Target size={20} data-id="element-2580" data-mp="1lyii2t6twiob" />} color="text-[#30718D]" isActive={priority === 'all' && skill === 'All Skills'} onClick={() => {
          setPriority('all');
          setSkill('All Skills');
        }} data-id="element-2579" data-mp="u6ub6m6tkocv" />
          <StatCard label="Critical issues" value={String(counts.critical)} icon={<AlertTriangle size={20} data-id="element-2582" data-mp="1wh9fqckdf6xy" />} color="text-[#DB3725]" isActive={priority === 'critical' && skill === 'All Skills'} onClick={() => {
          setPriority('critical');
          setSkill('All Skills');
        }} data-id="element-2581" data-mp="20k630gkuvr88" />
          <StatCard label="Improvement opportunities" value={String(counts.warning)} icon={<TrendingUp size={20} data-id="element-2584" data-mp="1cta5d2tpy1t2" />} color="text-[#b77808]" isActive={priority === 'warning' && skill === 'All Skills'} onClick={() => {
          setPriority('warning');
          setSkill('All Skills');
        }} data-id="element-2583" data-mp="1hno5fijbsx42" />
          <StatCard label="Success stories" value={String(counts.positive)} icon={<Star size={20} data-id="element-2586" data-mp="tx29uqqid5ay" />} color="text-[#007558]" isActive={priority === 'positive' && skill === 'All Skills'} onClick={() => {
          setPriority('positive');
          setSkill('All Skills');
        }} data-id="element-2585" data-mp="yuai9zlutuz0" />
        </section>

        <section aria-label="Filter insights" className="mb-5 flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm lg:flex-row lg:items-center" data-id="element-2587" data-mp="ts2yibgjnw1j">
          <div className="flex flex-wrap gap-1 rounded-lg bg-gray-50 p-1" role="group" aria-label="Priority filter" data-id="element-2588" data-mp="13f7s0d10egl">
            {([['all', 'All'], ['critical', 'Critical'], ['warning', 'Warning'], ['positive', 'Positive']] as const).map(([value, label]) => <button key={value} type="button" onClick={() => setPriority(value)} className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${priority === value ? value === 'critical' ? 'bg-[#DB3725] text-white' : value === 'warning' ? 'bg-[#F59E0B] text-white' : value === 'positive' ? 'bg-[#007558] text-white' : 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-white hover:text-gray-900'}`} data-id="element-2589" data-mp="3egqnvezhissz">
                {label}
                {value !== 'all' && ` (${counts[value]})`}
              </button>)}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto" data-id="element-2590" data-mp="23ormocuidch6">
            <SelectFilter label="Skill" value={skill} options={skills} onChange={setSkill} data-id="element-2591" data-mp="23ohvilxq8z9w" />
            <SelectFilter label="Learner" value={learner} options={learners} onChange={value => {
            setLearner(value);
            setPriority('all');
          }} data-id="element-2592" data-mp="2cp69jgn4ezms" />
            <ResetFiltersButton isActive={priority !== 'all' || skill !== 'All Skills' || learner !== 'All Learners'} onReset={() => {
            setPriority('all');
            setSkill('All Skills');
            setLearner('All Learners');
          }} data-id="element-2593" data-mp="idrjszu41071" />
          </div>
        </section>

        <section className="space-y-3" aria-live="polite" data-id="element-2594" data-mp="2b8aacwep5tw3">
          <AnimatePresence mode="popLayout" data-id="element-2595" data-mp="2b6phdb816j0c">
            {visible.map((insight, index) => <motion.article id={`insight-${insight.id}`} key={insight.id} layout initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            delay: index * 0.04
          }} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-2596" data-mp="2g8ycad4grv53" data-mpi={index}>
                <button type="button" onClick={() => toggleInsight(insight)} aria-expanded={expanded.has(insight.id)} className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50 sm:p-5" data-id="element-2597" data-mp="3u9b4in7upp18">
                  <PriorityIcon priority={insight.priority} data-id="element-2598" data-mp="gs23chf9892b" />
                  <span className="min-w-0 flex-1" data-id="element-2599" data-mp="gsu22145emwt">
                    <span className="mb-1 block text-sm font-semibold text-gray-900" data-id="element-2600" data-mp="c0ttxldxcnp5">
                      {insight.skill}
                      {insight.learner ? ` · ${insight.learner}` : ''}
                    </span>
                    <span className="block text-sm leading-relaxed text-gray-700" data-id="element-2601" data-mp="14iob07lcjrbs">
                      {insight.headline}
                    </span>
                    <span className="mt-2 flex items-center gap-1 text-xs text-gray-500" data-id="element-2602" data-mp="zp3eyl3bd7f8">
                      <Clock size={12} data-id="element-2603" data-mp="2ig0smk5b6woe" /> {insight.timestamp}
                    </span>
                  </span>
                  {expanded.has(insight.id) ? <ChevronUp size={18} className="mt-1 flex-none text-gray-500" data-id="element-2604" data-mp="2rdkw7zqn9526" /> : <ChevronDown size={18} className="mt-1 flex-none text-gray-500" data-id="element-2605" data-mp="31d20qdu5tcwk" />}
                </button>
                <AnimatePresence initial={false} data-id="element-2606" data-mp="3b1tmu5cjh2zk">
                  {expanded.has(insight.id) && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2607" data-mp="3f4gjqko2he3m">
                      <div className="border-t border-gray-100 px-4 pb-5 pt-4 sm:px-5" data-id="element-2608" data-mp="38zdtlvj6sx78">
                        <div className="grid gap-3 sm:grid-cols-3" data-id="element-2609" data-mp="34y26pxrp6q2q">
                          {insight.metrics.map(metric => <div key={metric.label} className="rounded-lg bg-gray-50 p-3" data-id="element-2610" data-mp="2h9j15q018rec">
                              <p className="text-xs font-medium text-gray-500" data-id="element-2611" data-mp="2q1b83qpulie6">
                                {metric.label}
                              </p>
                              <p className="mt-1 text-sm font-bold text-gray-900" data-id="element-2612" data-mp="2l7yltn8n5nol">
                                {metric.value}
                              </p>
                            </div>)}
                        </div>
                        <div className="mt-4" data-id="element-2613" data-mp="1jdx7so434u3e">
                          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-2614" data-mp="1jcvmzrx9ot8h">
                            What the data shows
                          </h2>
                          <ul className="mt-2 space-y-2" data-id="element-2615" data-mp="19ilsa3kuriul">
                            {insight.signals.map(signal => <li key={signal} className="flex gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm text-gray-700" data-id="element-2616" data-mp="1edivuuzd5003">
                                <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#30718D]" data-id="element-2617" data-mp="1s68m5blxcr30" />
                                {signal}
                              </li>)}
                          </ul>
                        </div>
                        <div className="mt-4 border-t border-gray-100 pt-4" data-id="element-2618" data-mp="lqy8s6px7a2c">
                          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-2619" data-mp="lotucuueasnt">
                            Suggested actions
                          </h2>
                          <div className="mt-2 flex flex-wrap gap-2" data-id="element-2620" data-mp="3mq5ubc0hb8ry">
                            {insight.actions.map(action => <button key={action} type="button" onClick={() => openPhoebeChat({
                        prompt: `Help me plan this action for ${insight.skill}: ${action}.`,
                        response: actionResponse(insight, action),
                        ...actionFollowUps(action)
                      })} className="rounded-lg border border-[#30718D]/30 bg-[#30718D]/5 px-3 py-2 text-left text-xs font-semibold text-[#30718D] transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2621" data-mp="3dq0o3570ugfd">
                                {action}
                              </button>)}
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.article>)}
          </AnimatePresence>
          {visible.length === 0 && <div className="rounded-xl border border-gray-100 bg-white px-6 py-12 text-center shadow-sm" data-id="element-2622" data-mp="2fejcx85xh0tc">
              <Filter size={36} className="mx-auto mb-3 text-gray-300" data-id="element-2623" data-mp="2u35w5kyoecpu" />
              <h2 className="text-base font-semibold text-gray-900" data-id="element-2624" data-mp="2p54bivllq5c9">
                No insights match these filters
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-2625" data-mp="335osdm7yhr34">
                Try choosing a different learner, skill, or priority.
              </p>
              <button type="button" onClick={() => {
            setPriority('all');
            setSkill('All Skills');
            setLearner('All Learners');
          }} className="mt-4 rounded-lg px-3 py-2 text-sm font-semibold text-[#30718D] hover:bg-[#30718D]/10" data-id="element-2626" data-mp="3670czgge8c2g">
                Clear filters
              </button>
            </div>}
        </section>
      </main>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  color,
  isActive,
  onClick
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} aria-pressed={isActive} aria-label={`Show ${label.toLowerCase()}`} className={`rounded-xl border bg-white p-4 text-left shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${isActive ? 'border-[#30718D] ring-1 ring-[#30718D]/20' : 'border-gray-100 hover:border-[#30718D]/40 hover:bg-[#30718D]/[0.02]'}`} data-id="element-2627" data-mp="7p4nfwvb3bhp">
      <span className={`flex items-center justify-between ${color}`} data-id="element-2628" data-mp="ltloctqe421j">
        {icon}
        <span className="text-2xl font-bold text-gray-900" data-id="element-2629" data-mp="hs9vmfc7z0p4">{value}</span>
      </span>
      <span className="mt-2 block text-xs font-medium text-gray-600" data-id="element-2630" data-mp="cu8e3yufz9fr">
        {label}
      </span>
    </button>;
}
function PriorityIcon({
  priority
}: {
  priority: Priority;
}) {
  const classes = priority === 'critical' ? 'bg-[#DB3725]/10 text-[#DB3725]' : priority === 'warning' ? 'bg-[#F59E0B]/10 text-[#b77808]' : 'bg-[#007558]/10 text-[#007558]';
  const Icon = priority === 'positive' ? Star : priority === 'warning' ? TrendingUp : AlertTriangle;
  return <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${classes}`} data-id="element-2631" data-mp="3latdcnx9uo5o">
      <Icon size={18} data-id="element-2632" data-mp="3g9xdclg1jm5j" />
    </span>;
}
function SelectFilter({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const Icon = label === 'Skill' ? Target : Users;
  const widthClass = label === 'Skill' ? 'sm:w-64' : 'sm:w-48';
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  return <div className={`relative w-full ${widthClass}`} ref={dropdownRef} data-id="element-2633" data-mp="1mpu0xqgqtouw">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} aria-haspopup="listbox" className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2634" data-mp="6cwrwnf801nk">
        <Icon size={18} className="flex-none text-[#30718D]" data-id="element-2635" data-mp="csn761u88huo" />
        <span className="flex-1 whitespace-nowrap text-left text-gray-700" data-id="element-2636" data-mp="cs3yzcudp7tu">
          {value}
        </span>
        <ChevronDown size={16} className={`flex-none text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-2637" data-mp="7rpv86zt8k1m" />
      </button>
      {isOpen && <div role="listbox" aria-label={`${label} filter`} className="absolute left-0 z-20 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-2638" data-mp="3c9v9exq4d0gr">
          {options.map(option => <button key={option} type="button" role="option" aria-selected={option === value} onClick={() => {
        onChange(option);
        setIsOpen(false);
      }} className={`w-full border-b border-gray-100 px-4 py-3 text-left text-sm transition-colors last:border-b-0 ${option === value ? 'bg-[#30718D]/5 font-semibold text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2639" data-mp="2ximh2guu10zx">
              {option}
            </button>)}
        </div>}
    </div>;
}
function buildBriefing(insight: Insight) {
  return `${insight.skill} needs attention.\n\n${insight.headline}\n\nKey signals:\n${insight.signals.map(signal => `• ${signal}`).join('\n')}\n\nI can help you choose the best next action for the ${insight.affectedLearners} affected learner${insight.affectedLearners === 1 ? '' : 's'}.`;
}
function actionResponse(insight: Insight, action: string) {
  if (action === 'Email the 10 learners who are failing') {
    return `10 of 20 learners are currently failing CSS Fundamentals. Their shared gaps are Flexbox, Grid, and positioning.\n\nLearners to contact:\n• Ava Morgan\n• Daniel Kim\n• Elena Rodriguez\n• Jordan Lee\n• Marcus Bennett\n• Maya Patel\n• Noah Williams\n• Priya Shah\n• Sofia Nguyen\n• Tyler Brooks\n\nWould you like me to compose a supportive email for these 10 learners with targeted resources and an invitation to the next group study session?`;
  }
  return `Here’s a practical plan for “${action}” in ${insight.skill}:\n\n1. Start with the learners and concepts most affected: ${insight.signals[0].toLowerCase()}.\n2. Set a focused 30-minute support touchpoint this week.\n3. Share one targeted resource, then check progress within 7 days.\n\nSuccess measure: improve the current ${insight.metrics[1]?.label.toLowerCase() || 'performance metric'} from ${insight.metrics[1]?.value || 'its current level'} while reducing the core struggle pattern.`;
}
function actionFollowUps(action: string) {
  if (action !== 'Email the 10 learners who are failing') return {};
  return {
    suggestedQuestions: ['Yes, compose the email', 'Show the recipient list', 'Use a more supportive tone', 'Not now'],
    suggestionResponses: {
      'Yes, compose the email': `Here’s a draft you can review before sending:\n\nSubject: Extra support for CSS Fundamentals\n\nHi there,\n\nI noticed CSS Fundamentals has been challenging so far. I’ve gathered short resources on Flexbox, Grid, and positioning, and I’d like to invite you to our next group study session.\n\nWould you like me to personalize this draft with the study-session time before you send it?`,
      'Show the recipient list': `The email will go to these 10 learners:\n\n• Ava Morgan\n• Daniel Kim\n• Elena Rodriguez\n• Jordan Lee\n• Marcus Bennett\n• Maya Patel\n• Noah Williams\n• Priya Shah\n• Sofia Nguyen\n• Tyler Brooks`,
      'Use a more supportive tone': `I’ll make the outreach encouraging and specific: acknowledge that CSS can take practice, highlight the available resources, and frame the study session as optional extra support rather than a remediation requirement. Would you like me to draft that version?`,
      'Not now': `No problem — I’ll leave the learner list and suggested resources ready for when you’re ready to follow up.`
    }
  };
}